import React, { useState, useEffect, useRef } from 'react'
import { withRouter } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { toJS } from 'mobx'
import {
  MDBContainer,
  MDBCard,
  MDBCardHeader,
  MDBCardFooter,
  MDBCardBody,
  MDBRow,
  MDBCol,
  MDBCollapse,
  MDBBtn,
  MDBIcon,
} from 'mdb-react-ui-kit'
import MessagingStore from './../Messaging.store'
import { MessageFactory } from './../../../shared/factories'
import UserProfileService from '../../../shared/services/UserProfile.service'
import PaginationComponent from './../../../components/adminShared/Pagination.component'
import moment from 'moment'

import './MessagingWidget.scss'

const today = moment().startOf('day'),
  lastDate = moment(new Date()).subtract(7, 'days').endOf('day'),
  timezoneOffset = new Date().getTimezoneOffset(),
  WIDGET_PLACEHOLDER = {
    content: "<p><strong>All caught up!</strong><br />To read past messages, click 'View More'</p>\n",
    created_at: new Date(),
    creator: null,
    title: 'No new messages',
    updated_at: new Date(),
  }

const MessagingWidget = ({
  category,
  division_id,
  isHomepageBoard,
  hideIfEmpty,
  headerColor,
  viewerName,
  history,
  onSuccess,
  onError,
}) => {
  onSuccess = onSuccess && typeof onSuccess === 'function' ? onSuccess : () => {}
  onError = onError && typeof onError === 'function' ? onError : () => {}

  let Messages = toJS(MessagingStore.Messages[category]),
    Message = toJS(MessagingStore.Readonly[category])
  const isEmpty = !Message
  Message = Message ? Message : MessageFactory.create(WIDGET_PLACEHOLDER)

  const [showAdditional, setShowAdditional] = useState(false),
    { fetchMessagesByCategory } = MessagingStore,
    isAdmin = UserProfileService.isA(['internal-admin', 'internal-staff', 'system-admin'], true),
    isLeader = UserProfileService.isA(
      [
        'premier-agent',
        'district-leader',
        'regional-leader',
        'divisional-leader',
        'division-group',
        'admin-group',
        'internal',
      ],
      true
    ),
    className = headerColor ? `bg-${headerColor}` : '',
    createdBy = Message && Message.get('creator'),
    startDate = Message && Message.get('start_date'),
    createDate = Message && Message.get('created_at'),
    pagination = MessagingStore.pagination[category],
    postedOn = startDate ? moment.utc(startDate).format('MM/DD/YYYY') : moment.utc(createDate).format('MM/DD/YYYY'),
    hasFetched = MessagingStore.hasFetched[category]

  const lastDivisionIdRef = useRef(division_id)

  useEffect(() => {
    if (category && category !== 'division') {
      MessagingStore.hasFetched[category] = false
      MessagingStore.pagination[category] = { ...MessagingStore.pagination[category], page: 0 }
    }
    return () => {
      if (category && category !== 'division') {
        MessagingStore.hasFetched[category] = false
      }
    }
  }, [category])

  useEffect(() => {
    const divisionIdNum = division_id != null ? parseInt(division_id, 10) : null
    const storeDivisionNum = MessagingStore.lastDivisionId != null ? parseInt(MessagingStore.lastDivisionId, 10) : null
    const divisionMismatch = category === 'division' && divisionIdNum != null && storeDivisionNum !== divisionIdNum

    if (category === 'division' && division_id != null) {
      if (lastDivisionIdRef.current !== division_id || divisionMismatch) {
        lastDivisionIdRef.current = division_id
        fetchMessagesByCategory(category, division_id).then(
          () => onSuccess(),
          () => onError()
        )
      }
    } else if (category && !hasFetched) {
      fetchMessagesByCategory(category, division_id).then(
        () => onSuccess(),
        () => onError()
      )
    }
  }, [division_id, category, hasFetched])

  if (category === 'leader' && !isLeader) return <></>

  if (hideIfEmpty && isEmpty) return <></>

  return (
    <MDBCard className={`MessagingWidgetComponent ${className}`}>
      <MDBCardHeader>
        <h5 className='text-uppercase text-truncate text-center'>
          {viewerName ? `${viewerName} ` : ''}Message Board - Hot Off The Press!
        </h5>
        {isAdmin ? (
          <MDBIcon
            fas
            icon='edit'
            className='message-edit'
            onClick={event => {
              let msgId = Message?.id(),
                cat = msgId && Message?.get('category') ? Message.get('category') : category,
                url = ''

              // Division messages go to /division-messages page
              if (cat === 'division') {
                url = `/division-messages`
                url += msgId > 0 ? `?id=${msgId}` : ''
              } else {
                // Other categories go to /manage-messaging
                url = `/manage-messaging`
                url += msgId > 0 ? `?id=${msgId}` : ''
                url += `#${cat}-md`
              }

              window.location.href = url
            }}
          />
        ) : (
          <></>
        )}
      </MDBCardHeader>
      <MDBCardBody>
        {
          <div
            className='body-content'
            dangerouslySetInnerHTML={{
              __html: Message && Message.get('content') ? Message.get('content') : '',
            }}
          ></div>
        }
        <MDBRow className='controls'>
          <MDBCol size='12' sm='6'>
            {Message ? (
              <div className='creator'>
                <table>
                  <tbody>
                    <tr>
                      <td>{createdBy ? 'Created By:' : ''}</td>
                      <td>{createdBy}</td>
                    </tr>
                    <tr>
                      <td>{postedOn ? 'Posted On:' : ''}</td>
                      <td>{postedOn}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            ) : (
              <></>
            )}
          </MDBCol>
          <MDBCol size='12' sm='6'>
            {Messages && Messages.length ? (
              <MDBBtn
                className='view-more-btn'
                size='sm'
                color='red'
                type='button'
                onClick={() => setShowAdditional(!showAdditional)}
              >
                {showAdditional ? 'View Less' : 'View More'}
              </MDBBtn>
            ) : (
              <></>
            )}
          </MDBCol>
        </MDBRow>
      </MDBCardBody>
      <MDBCardFooter className={showAdditional ? 'show' : ''}>
        <MDBCollapse open={showAdditional}>
          {(Messages && Messages.length ? Messages : []).map((Msg, i) => {
            const msgStartDate = Msg.get('start_date')
            const msgCreateDate = Msg.get('created_at')
            const msgPostedOn = msgStartDate
              ? moment.utc(msgStartDate).format('MM/DD/YYYY')
              : msgCreateDate
              ? moment.utc(msgCreateDate).format('MM/DD/YYYY')
              : ''
            return (
              <React.Fragment key={`msg-idx-${Msg.id()}-${i}`}>
                <MDBRow>
                  <MDBCol size='12' className='historical-msg'>
                    <div className='historical-msg-header'>
                      <h5 className='text-primary'>{Msg.get('title')}</h5>
                      {msgPostedOn && <span className='historical-msg-date'>Posted On: {msgPostedOn}</span>}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: Msg.get('content') }}></div>
                  </MDBCol>
                </MDBRow>
                <hr className={'my-1'} />
              </React.Fragment>
            )
          })}
          {pagination && parseInt(pagination.total) && (
            <>
              <div className='pagination-wrapper'>
                <PaginationComponent
                  key={pagination.page}
                  pagination={pagination}
                  onPageSelect={(page, perPage) => {
                    MessagingStore.pagination[category].page = page
                    MessagingStore.pagination[category].per_page = perPage
                    fetchMessagesByCategory(category, division_id)
                  }}
                />
              </div>
            </>
          )}
        </MDBCollapse>
      </MDBCardFooter>
    </MDBCard>
  )
}

export default observer(MessagingWidget)
